import React from "react";
import { Box, Flex, HStack, LinkBox, LinkOverlay, Text } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { workbookPath } from "./workbookRouting";

const normalizeStep = (currentStep) => {
  if (currentStep === 1 || currentStep === "overview") return 1;
  if (currentStep === 2 || currentStep === "interpretation") return 2;
  if (
    currentStep === 3 ||
    currentStep === "widgets" ||
    currentStep === "practices"
  ) {
    return 3;
  }

  return 1;
};

const buildRouteState = ({ pageId, organizationSurveyId, interpretationId, readOnly }) => ({
  ...(organizationSurveyId ? { organizationSurveyId } : {}),
  ...(pageId ? { pageId } : {}),
  ...(interpretationId ? { interpretationId } : {}),
  ...(readOnly ? { readOnly: true } : {}),
});

const buildSteps = ({ pageId, organizationSurveyId, interpretationId, routePrefix }) => [
  {
    number: 1,
    label: "Workbook Overview",
    to: workbookPath(routePrefix),
    canNavigate: true,
  },
  {
    number: 2,
    label: "Interpretation",
    to: pageId ? workbookPath(routePrefix, `pages/${pageId}`) : null,
    canNavigate: Boolean(pageId),
  },
  {
    number: 3,
    label: "Next Day Practices",
    to: pageId ? workbookPath(routePrefix, `pages/${pageId}/widgets`) : null,
    canNavigate: Boolean(pageId && organizationSurveyId && interpretationId),
  },
];

const StepContent = ({ step, status }) => {
  const isActive = status === "active";
  const isDone = status === "done";
  const isDisabled = status === "disabled";

  return (
    <HStack spacing={2} flexShrink={0}>
      <Flex
        w="24px"
        h="24px"
        borderRadius="full"
        align="center"
        justify="center"
        fontSize="xs"
        fontWeight="bold"
        borderWidth="2px"
        borderColor={isDone ? "green.500" : isActive ? "blue.500" : "gray.200"}
        bg={isDone ? "green.500" : isActive ? "blue.500" : "white"}
        color={isDone || isActive ? "white" : "gray.500"}
      >
        {step.number}
      </Flex>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color={
          isDone
            ? "green.600"
            : isActive
            ? "blue.600"
            : isDisabled
            ? "gray.400"
            : "gray.600"
        }
        whiteSpace="nowrap"
      >
        {step.label}
      </Text>
    </HStack>
  );
};

const WorkbookStepNavigation = ({
  pageId,
  organizationSurveyId,
  interpretationId,
  currentStep,
  routePrefix,
  readOnly = false,
}) => {
  const activeStep = normalizeStep(currentStep);
  const routeState = buildRouteState({
    pageId,
    organizationSurveyId,
    interpretationId,
    readOnly,
  });
  const steps = buildSteps({ pageId, organizationSurveyId, interpretationId, routePrefix });

  return (
    <Flex
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
      px={{ base: 4, md: 6 }}
      py={4}
      align="center"
      gap={3}
      boxShadow="sm"
      overflowX="auto"
    >
      {steps.map((step, index) => {
        const status =
          step.number < activeStep
            ? "done"
            : step.number === activeStep
            ? "active"
            : step.canNavigate
            ? "available"
            : "disabled";
        const canNavigate = step.canNavigate && status !== "active";

        return (
          <React.Fragment key={step.number}>
            {canNavigate ? (
              <LinkBox
                as="span"
                borderRadius="md"
                _hover={{ textDecoration: "none", bg: "gray.50" }}
                px={1}
                py={1}
              >
                <LinkOverlay as={RouterLink} to={step.to} state={routeState}>
                  <StepContent step={step} status={status} />
                </LinkOverlay>
              </LinkBox>
            ) : (
              <Box
                as="span"
                px={1}
                py={1}
                cursor={status === "disabled" ? "not-allowed" : "default"}
                aria-current={status === "active" ? "step" : undefined}
                aria-disabled={status === "disabled" ? "true" : undefined}
              >
                <StepContent step={step} status={status} />
              </Box>
            )}

            {index < steps.length - 1 ? (
              <Box h="1px" bg="gray.200" flex="1" minW="24px" />
            ) : null}
          </React.Fragment>
        );
      })}
    </Flex>
  );
};

export default WorkbookStepNavigation;
